import React, { useEffect, useState } from 'react';
import { AUTH_CHANGED_EVENT, getCachedSessionUser, getStoredToken } from '../../services/authService';
import { fetchResumeRateLimitInfo } from '../../services/resumeService';

function formatZoneLabel(zone) {
  if (!zone) return null;
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(new Date());
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value;
    return tzName ? `${zone} (${tzName})` : zone;
  } catch {
    return zone;
  }
}

function isAdminUser() {
  return getCachedSessionUser()?.role === 'ADMIN';
}

/**
 * Contractor evaluation limits on the Resume hub. Hidden for ADMIN (exempt).
 * limitError is shown only when an evaluate request hits the limit.
 */
export default function ResumeRateLimitNotice({ limitError = null }) {
  const [isAdmin, setIsAdmin] = useState(isAdminUser);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsAdmin(isAdminUser());
      if (getStoredToken() && isAdminUser()) {
        if (!cancelled) {
          setConfig(null);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      try {
        const data = await fetchResumeRateLimitInfo();
        if (!cancelled) setConfig(data);
      } catch {
        if (!cancelled) setConfig(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    window.addEventListener(AUTH_CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_CHANGED_EVENT, load);
    };
  }, []);

  if (isAdmin && !limitError) return null;

  const showInfo = !loading && config?.enabled;
  const daily = Number(config?.dailyLimitPerCandidate) || 0;
  const weekDays = Number(config?.maxDaysPerWeekPerCandidate) || 0;
  const showLimits = showInfo && daily >= 1 && weekDays >= 1;

  if (loading && !limitError) {
    return (
      <div className="resume-hub__rate-limit-wrap">
        <p className="resume-hub__rate-limit-loading">Loading evaluation limits…</p>
      </div>
    );
  }

  if (!showLimits && !limitError) return null;

  const zoneLabel = showLimits ? formatZoneLabel(config.rateLimitZone) : null;
  const dailyPhrase = (
    <>
      up to <strong>{daily}</strong> evaluation{daily === 1 ? '' : 's'} per day
    </>
  );
  const weekPhrase = weekDays < 7 ? (
    <>
      , on up to <strong>{weekDays}</strong> day{weekDays === 1 ? '' : 's'} per week (Monday–Sunday)
    </>
  ) : null;

  return (
    <div className="resume-hub__rate-limit-wrap">
      {showLimits ? (
        <div className="resume-hub__rate-limit" role="note">
          <p className="resume-hub__rate-limit-title">Contractor evaluation limits</p>
          <p className="resume-hub__rate-limit-text">
            You may run {dailyPhrase}
            {weekPhrase}
            . </p>
          {zoneLabel ? (
            <p className="resume-hub__rate-limit-meta">Resets use {zoneLabel}.</p>
          ) : null}
        </div>
      ) : null}

      {limitError ? (
        <div className="resume-hub__rate-limit resume-hub__rate-limit--exceeded" role="alert">
          <p className="resume-hub__rate-limit-title">Evaluation limit reached</p>
          <p className="resume-hub__rate-limit-text">{limitError}</p>
        </div>
      ) : null}
    </div>
  );
}
